import type { IncidentFileFieldTypeDto } from "@phrmpc/infrastructure/phrmpc/models";

// Maps this form's document slots to the live IncidentFileFieldType lookup by
// `code` (see getAllIncidentFileFieldTypes()). TFS Bug 157740 tracked two gaps,
// both now resolved (see the bug's comment thread for the backend team's
// clarification, confirmed against api's IncidentFileFieldTypeEnum.cs):
// - Incident Report Attachment got its own backend code (fixed via PR #2784).
// - AWOL's second Notice of Hearing ("NOH 2") is by design NOT a distinct
//   type - it reuses NOH_LETTER (same as NOH 1), with the same prerequisite
//   (signed AWOL Letter), not a sequential dependency on NOH 1. The backend
//   already supports up to 2 unsigned + 2 signed NOH_LETTER uploads per AWOL
//   record; the front-end distinguishes NOH 1 vs NOH 2 by upload order
//   (see IncidentRecordFileContext's `getFile` matchIndex parameter), not by
//   fileFieldTypeId.
export type FileFieldTypeKey =
  | "INCIDENT_REPORT_ATTACHMENT"
  | "EVIDENCE"
  | "NOTICE_OF_EXPLANATION"
  | "NOTICE_OF_HEARING"
  | "CASE_SUMMARY"
  | "NOTICE_OF_DECISION_AND_ACTION"
  | "RESIGNATION"
  | "TERMINATION"
  | "AWOL_LETTER"
  | "AWOL_NOTICE_OF_HEARING_1"
  | "AWOL_NOTICE_OF_HEARING_2"
  | "AWOL_NOTICE_OF_DECISION_AND_ACTION"
  | "TRANSFER_BACK_TO_WORK_LETTER";

const FILE_FIELD_TYPE_CODE: Record<FileFieldTypeKey, string> = {
  INCIDENT_REPORT_ATTACHMENT: "INCIDENT_REPORT_ATTACHMENT",
  EVIDENCE: "EVIDENCE",
  NOTICE_OF_EXPLANATION: "NTE_LETTER",
  NOTICE_OF_HEARING: "NOH_LETTER",
  CASE_SUMMARY: "CASE_SUMMARY",
  NOTICE_OF_DECISION_AND_ACTION: "NODA_LETTER",
  RESIGNATION: "RESIGNATION_LETTER",
  TERMINATION: "TERMINATION_LETTER",
  AWOL_LETTER: "AWOL_LETTER",
  // Backend only has one Notice of Hearing type for AWOL - reused here since
  // only one of the Minor/Major or AWOL sections is ever visible per record,
  // and NOH 1/NOH 2 share this same type id by design (see comment above).
  AWOL_NOTICE_OF_HEARING_1: "NOH_LETTER",
  AWOL_NOTICE_OF_HEARING_2: "NOH_LETTER",
  AWOL_NOTICE_OF_DECISION_AND_ACTION: "NODA_LETTER",
  TRANSFER_BACK_TO_WORK_LETTER: "TBWL_LETTER",
};

export const buildFileFieldTypeMap = (
  fileFieldTypes: IncidentFileFieldTypeDto[],
): Record<FileFieldTypeKey, number> => {
  const idByCode = new Map(
    fileFieldTypes.map((t) => [t.code, t.id] as const),
  );
  const idForCode = (code: string): number => idByCode.get(code) ?? 0;

  const map = {} as Record<FileFieldTypeKey, number>;
  for (const key of Object.keys(
    FILE_FIELD_TYPE_CODE,
  ) as FileFieldTypeKey[]) {
    map[key] = idForCode(FILE_FIELD_TYPE_CODE[key]);
  }
  return map;
};
